import { AlertFactory, AlertState } from 'alerts/Alert'
import {
  CallCenterSearchResult,
  CallCenterWorkspaceMemberMatch,
} from 'call-center/models/CallCenterSearchResult'
import { CallCenterSearchService } from 'call-center/services/CallCenterSearchService'
import { CallCenterSelectionViewModel } from 'call-center/CallCenterSelectionViewModel'
import { CallCenterJobHistoryViewModel } from 'call-center/CallCenterJobHistoryViewModel'
import { filterCallCenterWorkspaceMembersMatches } from 'call-center/utils'
import { FreshbooksCallCenterAPI } from 'freshbooks/API'
import MemberStore from 'iam/MemberStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import { IAMAPI } from 'iam/API'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { Subscription } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'
import { determineCallCenterSource } from './services/CallCenterResultMerger'

const SEARCH_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 750

export class CallCenterCustomerLookupViewModel {
  private searchService: CallCenterSearchService

  private subscription = new Subscription()

  callCenterSelectionVM: CallCenterSelectionViewModel

  callCenterJobHistoryVM: CallCenterJobHistoryViewModel

  searchText = new SearchTextViewModel(SEARCH_DEBOUNCE_MS)

  scroller = new InfiniteScrollProvider<CallCenterSearchResult, string>({
    limit: SEARCH_LIMIT,
    startFetchTaskAsPending: true,
    fetchItems: (paging, cancellationToken, query) =>
      this.fetchSearchResults(query ?? '', paging, cancellationToken),
  })

  @observable
  lastQuery = ''

  constructor(
    freshbooksCallCenterAPI: FreshbooksCallCenterAPI,
    workspaceMembershipStore: WorkspaceMembershipStore,
    workspaceStore: WorkspaceStore,
    iamAPI: IAMAPI,
    memberStore: MemberStore,
    jobSearchStore: JobSearchStore,
    public publishedLegendStore: PublishedLegendStore,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)

    this.searchService = new CallCenterSearchService(
      freshbooksCallCenterAPI,
      workspaceMembershipStore,
      iamAPI,
      memberStore,
    )

    this.callCenterSelectionVM = new CallCenterSelectionViewModel(
      workspaceMembershipStore,
      workspaceStore,
    )

    this.callCenterJobHistoryVM = new CallCenterJobHistoryViewModel(
      jobSearchStore,
    )
  }

  @computed
  get results() {
    return this.scroller.items
  }

  @computed
  get isSearching() {
    return (
      this.lastQuery.length > 0 &&
      this.results.length === 0 &&
      this.scroller.fetch.pending
    )
  }

  @computed
  get shouldShowResultsLoader() {
    if (!this.lastQuery) {
      return false
    }

    if (this.scroller.fetch.pending && this.results.length === 0) {
      return false
    }

    return this.results.length > 0 && !this.scroller.isDone
  }

  @computed
  get shouldShowEmptyState() {
    return !this.isSearching && this.results.length === 0
  }

  @computed
  get showAddClientButton() {
    return this.shouldShowEmptyState && this.lastQuery.length > 0
  }

  @action.bound
  setLastQuery(value: string) {
    this.lastQuery = value
  }

  @action.bound
  clearResults() {
    this.scroller.reset(false)
    this.searchService.reset()
    this.callCenterSelectionVM.clear()
    this.callCenterJobHistoryVM.clear()
  }

  activate() {
    this.subscription.unsubscribe()

    this.subscription = this.searchText.debouncedValue$
      .pipe(
        map((value) => value.trim()),
        distinctUntilChanged(),
      )
      .subscribe((value) => {
        void this.performSearch(value)
      })
  }

  deactivate() {
    this.subscription.unsubscribe()
  }

  async performSearch(query: string) {
    this.setLastQuery(query)

    if (!query) {
      this.clearResults()
      return
    }

    this.clearResults()
    this.searchService.prepareWorkspaceContext()

    try {
      await this.scroller.fetch(query)
    } catch (error) {
      if (query !== this.lastQuery) {
        return
      }
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Unable to search contacts. Please try again later.')
        .autoDismiss()
        .show()
      this.clearResults()
    }
  }

  async selectResult(result: CallCenterSearchResult) {
    // Get initial workspace matches.
    let workspaceMatches = filterCallCenterWorkspaceMembersMatches(
      result.workspaceMembers,
    )

    // If no workspace members, try to find them by email.
    if (workspaceMatches.length === 0) {
      workspaceMatches = await this.ensureWorkspaceMembersForResult(result)
    }

    // Set initial selection state.
    const initialMemberships = workspaceMatches.map((match) => ({
      workspace: match.workspace,
      member: match.member,
    }))

    this.callCenterSelectionVM.setSelectedResult(result, initialMemberships)

    // Load jobs for the first member if available.
    const member = workspaceMatches[0]?.member
    if (!member) {
      this.callCenterJobHistoryVM.clear()
      return
    }

    const userPublicId = Id.assert(member.user.publicId)
    this.callCenterJobHistoryVM.setUserAndLoadJobs(userPublicId)

    // Load full membership details.
    try {
      await this.callCenterSelectionVM.loadMembershipDetails(userPublicId)
    } catch (error) {
      console.error('Failed to load membership details', error)
    }
  }

  private async fetchSearchResults(
    query: string,
    paging: CursorPageQuery,
    cancellationToken: CancellationToken,
  ): Promise<CursorPaged<CallCenterSearchResult>> {
    if (!query || query !== this.lastQuery) {
      return CursorPaged.empty
    }

    const limit = paging.limit ?? SEARCH_LIMIT
    return this.searchService.search(query, limit, cancellationToken)
  }

  private async ensureWorkspaceMembersForResult(
    result: CallCenterSearchResult,
  ): Promise<CallCenterWorkspaceMemberMatch[]> {
    const contact = result.hubspotContact
    if (!contact?.email) {
      return result.workspaceMembers
    }

    const matches = await this.searchService.searchWorkspaceMembers(
      contact.email,
      SEARCH_LIMIT,
    )

    if (matches.length > 0) {
      runInAction(() => {
        result.workspaceMembers = matches
        // Update source using the result merger
        const merger = this.searchService.getResultMerger()
        const existingResult = merger.getResultByKey(result.key)
        if (existingResult) {
          existingResult.workspaceMembers = matches
          existingResult.source = determineCallCenterSource(existingResult)
        }
      })
    }

    return matches
  }
}
